<template>
  <v-container
    fluid
    class="d-flex justify-center align-center"
    style="height: 100%"
  >
    <v-card
      class="d-flex flex-column justify-center align-center"
      min-width="95%"
      elevation="0"
    >
      <v-card-title style="width: 100%" class="px-10 py-7">
        <v-row>
          <v-col cols="4">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              :label="$t('partners.search')"
              filled
              dense
              outlined
              hide-details
            ></v-text-field>
          </v-col>
          <v-col v-if="isAdmin" class="d-flex justify-end align-center">
            <v-btn
              small
              elevation="0"
              class="mx-2"
              fab
              dark
              color="yellow darken-2"
              @click="openDialog(null)"
            >
              <v-icon dark> mdi-plus </v-icon>
            </v-btn>
            <span class="font-weight-light text-subtitle-1 mr-lg-10">
              {{ $t("partners.newPartner") }}
            </span>
          </v-col>
        </v-row>
      </v-card-title>

      <PartnerDialog
        v-model="dialog"
        :partner="selectedPartner"
        @saved="getPartners"
      />

      <v-dialog v-model="deleteDialog" max-width="420px">
        <v-card>
          <v-card-title class="headline">
            {{ $t("partners.deleteTitle") }}
          </v-card-title>
          <v-card-text>
            {{ $t("partners.deleteConfirm", { company: selectedCompany }) }}
            <v-alert v-if="error" type="error" dense text class="mt-4">
              {{ $t("partners.errorOccurred") }}
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="deleteDialog = false">
              {{ $t("partners.close") }}
            </v-btn>
            <v-btn
              :disabled="loading"
              color="red darken-1"
              text
              @click="deletePartner"
            >
              {{ $t("partners.delete") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-data-table
        style="width: 100%"
        height="75vh"
        :loading="loading"
        :headers="translatedHeaders"
        :items="filteredItems"
        :page.sync="page"
        :items-per-page="itemsPerPage"
        hide-default-footer
        mobile-breakpoint="768"
        @page-count="pageCount = $event"
      >
        <template v-slot:[`item.company`]="{ item }">
          {{ item.company }}
          <v-chip
            v-if="item.is_spotlight"
            x-small
            color="#2b58e2"
            text-color="white"
            class="ml-2"
          >
            {{ $t("partners.spotlightChip") }}
          </v-chip>
        </template>
        <template v-slot:[`item.email`]="{ item }">
          <a v-if="item.email" :href="`mailto:${item.email}`">
            {{ item.email }}
          </a>
        </template>
        <template v-slot:[`item.website`]="{ item }">
          <a
            v-if="item.website"
            :href="item.website"
            target="_blank"
            rel="noopener"
          >
            {{ item.website }}
          </a>
        </template>
        <template v-slot:[`item.id`]="{ item }">
          <div class="d-flex justify-center align-center">
            <v-btn class="edit-button mr-2" @click="openDialog(item)">
              {{ $t("partners.edit") }}
            </v-btn>
            <v-btn icon color="red darken-1" @click="confirmDelete(item)">
              <v-icon>mdi-delete-outline</v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>
      <div class="text-center py-5">
        <v-pagination
          total-visible="5"
          v-model="page"
          :length="pageCount"
        ></v-pagination>
      </div>
    </v-card>
  </v-container>
</template>

<script>
import * as Sentry from "@sentry/vue";
import partners from "@/api/partners";
import PartnerDialog from "./PartnerDialog.vue";

const ADMIN_ROLES = ["superuser", "developer"];
const SEARCHABLE_FIELDS = [
  "company",
  "service",
  "contact",
  "phone_number",
  "email",
  "website",
];

export default {
  name: "Partners",
  components: { PartnerDialog },
  data() {
    return {
      loading: false,
      error: false,
      page: 1,
      pageCount: 0,
      itemsPerPage: 10,
      search: "",
      items: [],
      dialog: false,
      deleteDialog: false,
      selectedPartner: null,
    };
  },
  computed: {
    isAdmin() {
      return ADMIN_ROLES.includes(this.$store.getters.getUser.role);
    },
    translatedHeaders() {
      const headers = SEARCHABLE_FIELDS.map((value) => ({
        text: this.$t(`partners.headers.${value}`),
        value,
        sortable: false,
      }));
      if (this.isAdmin) {
        headers.push({ text: "", value: "id", width: "15%", sortable: false });
      }
      return headers;
    },
    filteredItems() {
      const searchLower = this.search.toLowerCase();
      return this.items.filter((item) =>
        SEARCHABLE_FIELDS.some((field) =>
          (item[field] ?? "").toLowerCase().includes(searchLower),
        ),
      );
    },
    selectedCompany() {
      return this.selectedPartner?.company ?? "";
    },
  },
  methods: {
    async getPartners() {
      this.loading = true;
      try {
        const response = await partners.fetchPartners();
        this.items = response.code == 200 ? response.data : [];
      } catch (error) {
        Sentry.captureException(error);
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    openDialog(partner) {
      this.selectedPartner = partner;
      this.dialog = true;
    },
    confirmDelete(partner) {
      this.selectedPartner = partner;
      this.error = false;
      this.deleteDialog = true;
    },
    async deletePartner() {
      this.loading = true;
      this.error = false;
      try {
        const response = await partners.deletePartner(this.selectedPartner.id);
        if (response.code == 200) {
          this.deleteDialog = false;
          await this.getPartners();
        } else {
          this.error = true;
        }
      } catch (error) {
        Sentry.captureException(error);
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
  },
  async activated() {
    await this.getPartners();
  },
};
</script>

<style scoped>
::v-deep table tbody tr td {
  padding: 20px 16px !important;
}

@media (min-width: 768px) {
  ::v-deep table tbody tr td:first-child,
  ::v-deep table thead tr th:first-child {
    padding-left: 3rem !important;
  }
  ::v-deep table tbody tr td:last-child,
  ::v-deep table thead tr th:last-child {
    padding-right: 3rem !important;
  }

  ::v-deep table tbody tr td {
    border: none !important;
  }
}

::v-deep
  .theme--light.v-data-table
  > .v-data-table__wrapper
  > table
  > thead
  > tr:last-child
  > th {
  border-top: thin solid rgba(0, 0, 0, 0.12);
}

::v-deep table thead tr th {
  font-size: 14px !important;
  font-weight: 500 !important;
}

.edit-button {
  background-color: #2b58e2 !important;
  color: white !important;
  text-transform: none;
  font-family: sans-serif !important;
  height: 30px !important;
}
</style>
